// "Seat setup": the engine's first flow (build order, increment 2, "Seat invitation with guided
// onboarding"). Inviting someone to a seat on the chart sends it; the inviter fills what they
// know, the invitee completes their part. Its answers are the seat's own fields, so seat
// completeness ("Seat setup: 57% complete") moves as it is filled. Pay, bank and ID numbers are
// entered only by the person, in the secure step (sealed with the keyring; seen by the person,
// owners and finance; never by the helper).
import type { FlowDef } from "./types";

const DAYS = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
] as const;

/** The tool groups a seat may ask for (least privilege: none until someone picks one). */
export const TOOL_OPTIONS = [
  { value: "chat", label: "Chat" },
  { value: "email", label: "Email" },
  { value: "calendar", label: "Calendar" },
  { value: "crm", label: "CRM" },
  { value: "docs", label: "Docs" },
  { value: "boards", label: "Boards" },
  { value: "finance", label: "Finance" },
] as const;

export const SEAT_SETUP: FlowDef = {
  key: "seat_setup",
  title: "Seat setup",
  callToAction: "Complete your seat setup",
  chapters: [
    {
      key: "role",
      title: "Role & reporting",
      steps: [
        {
          key: "role",
          title: "Role, title, reports to",
          info: "Where the seat sits on the chart.",
          fields: [
            {
              key: "title",
              label: "Title",
              info: "The seat's name on the chart, e.g. Account manager.",
              type: "text",
              maxLength: 200,
              required: true,
              bind: { kind: "seat", column: "title" },
              fill: ["manager"],
            },
            {
              key: "role_id",
              label: "Role",
              info: "The role this seat is an instance of; it brings the role's skills.",
              type: "role",
              required: true,
              bind: { kind: "seat", column: "role_id" },
              fill: ["manager"],
            },
            {
              key: "reports_to",
              label: "Reports to",
              info: "The seat above this one. Approvals and escalations go there.",
              type: "seat",
              required: true,
              // The top of the chart (a person's seat with reports and no supervisor) ends the chain.
              optionalWhen: { field: "_top_of_chart", equals: true },
              bind: { kind: "seat", column: "supervisor_seat_id" },
              fill: ["manager"],
            },
            {
              key: "staff",
              label: "Staff seat",
              info: "An assistant seat beside its supervisor, not in the line below.",
              type: "boolean",
              bind: { kind: "seat", column: "staff" },
              fill: ["manager"],
              when: { field: "reports_to", filled: true },
            },
          ],
        },
      ],
    },
    {
      key: "duties",
      title: "Responsibilities & KPIs",
      steps: [
        {
          key: "duties",
          title: "Responsibilities & KPIs",
          info: "What the seat owns and how it is measured.",
          hideForAccess: ["client_viewer"],
          fields: [
            {
              key: "responsibilities",
              label: "Responsibilities",
              info: "The outcomes this seat owns. At least one.",
              type: "linked",
              required: true,
              bind: { kind: "linked", editor: "responsibilities" },
              fill: ["manager"],
            },
            {
              key: "kpis",
              label: "KPIs",
              info: "The scorecard numbers of this seat.",
              type: "linked",
              bind: { kind: "linked", editor: "kpis" },
              fill: ["manager"],
            },
          ],
        },
      ],
    },
    {
      key: "schedule",
      title: "Schedule & hours",
      steps: [
        {
          key: "hours",
          title: "Hours",
          info: "When this seat works.",
          fields: [
            {
              key: "hours_per_week",
              label: "Hours a week",
              info: "Contracted or usual hours in a normal week.",
              type: "number",
              min: 1,
              max: 80,
              required: true,
              bind: { kind: "seat_setting", path: ["schedule", "hours_per_week"] },
              fill: ["manager", "assignee"],
            },
            {
              key: "work_days",
              label: "Work days",
              info: "The days this seat is usually on.",
              type: "multiselect",
              options: DAYS,
              required: true,
              bind: { kind: "seat_setting", path: ["schedule", "days"] },
              fill: ["manager", "assignee"],
            },
            {
              key: "schedule_kind",
              label: "Fixed hours",
              info: "Fixed: the same start and end each day. Flexible: any time on work days.",
              type: "select",
              options: [
                { value: "fixed", label: "Fixed" },
                { value: "flexible", label: "Flexible" },
              ],
              required: true,
              bind: { kind: "seat_setting", path: ["schedule", "kind"] },
              fill: ["manager", "assignee"],
            },
            {
              key: "start_time",
              label: "Starts",
              info: "Start of the working day (24 h).",
              type: "time",
              required: true,
              bind: { kind: "seat_setting", path: ["schedule", "start"] },
              fill: ["manager", "assignee"],
              when: { field: "schedule_kind", equals: "fixed" },
            },
            {
              key: "end_time",
              label: "Ends",
              info: "End of the working day (24 h).",
              type: "time",
              required: true,
              bind: { kind: "seat_setting", path: ["schedule", "end"] },
              fill: ["manager", "assignee"],
              when: { field: "schedule_kind", equals: "fixed" },
            },
            {
              key: "time_zone",
              label: "Time zone",
              info: "An IANA zone, e.g. Europe/Bucharest.",
              type: "text",
              maxLength: 64,
              pattern: "[A-Za-z_]+(/[A-Za-z0-9_+-]+){0,2}",
              patternHint: "an IANA zone like Europe/Bucharest",
              bind: { kind: "seat_setting", path: ["schedule", "time_zone"] },
              fill: ["manager", "assignee"],
            },
          ],
        },
      ],
    },
    {
      key: "access",
      title: "Tools & access",
      steps: [
        {
          key: "access",
          title: "Tools & access level",
          info: "What the seat uses and how much of the company it sees.",
          fields: [
            {
              key: "access_level",
              label: "Access level",
              info: "Member: the team's work. Admin: manages the company. Client viewer: read-only reports.",
              type: "select",
              options: [
                { value: "member", label: "Member" },
                { value: "admin", label: "Admin" },
                { value: "client_viewer", label: "Client viewer" },
              ],
              bind: { kind: "invitation_roles" },
              fill: ["manager"],
            },
            {
              key: "tools",
              label: "Tools",
              info: "Only what the job needs. More can be added later.",
              type: "multiselect",
              options: TOOL_OPTIONS,
              bind: { kind: "seat_setting", path: ["tools"] },
              fill: ["manager", "assignee"],
            },
          ],
        },
      ],
    },
    {
      key: "financials",
      title: "Financials",
      steps: [
        {
          key: "budget",
          title: "Budget",
          info: "The seat's spending limit.",
          see: ["manager", "finance"],
          fields: [
            {
              key: "budget",
              label: "Budget",
              info: "A budget on the seat (counts toward seat setup).",
              type: "linked",
              required: true,
              bind: { kind: "linked", editor: "budget" },
              // Budgets are written by managers (budgets.create); finance sees them.
              fill: ["manager"],
            },
          ],
        },
        {
          key: "pay",
          title: "Pay & details",
          info: "Entered by you only. Sealed; seen by you, owners and finance.",
          secure: true,
          see: ["assignee", "owner", "finance"],
          hideForAccess: ["client_viewer"],
          fields: [
            {
              key: "pay_cadence",
              label: "Paid",
              info: "How your pay is counted.",
              type: "select",
              options: [
                { value: "monthly", label: "Monthly" },
                { value: "hourly", label: "Hourly" },
                { value: "contract", label: "Per contract" },
              ],
              required: true,
              bind: { kind: "secure", name: "pay_cadence" },
              fill: ["assignee"],
            },
            {
              key: "pay_rate",
              label: "Rate",
              info: "The amount per month, hour or contract.",
              type: "money",
              required: true,
              bind: { kind: "secure", name: "pay_rate" },
              fill: ["assignee"],
            },
            {
              key: "pay_currency",
              label: "Currency",
              info: "Three letters, e.g. EUR.",
              type: "secret",
              maxLength: 3,
              pattern: "[A-Z]{3}",
              patternHint: "three capital letters, e.g. EUR",
              required: true,
              bind: { kind: "secure", name: "pay_currency" },
              fill: ["assignee"],
            },
            {
              key: "bank_account",
              label: "Bank account",
              info: "IBAN or account number for payouts.",
              type: "secret",
              maxLength: 64,
              pattern: "[A-Za-z0-9 -]{6,64}",
              patternHint: "letters, digits, spaces or dashes",
              required: true,
              bind: { kind: "secure", name: "bank_account" },
              fill: ["assignee"],
            },
            {
              key: "id_number",
              label: "ID number",
              info: "National ID or tax number, as on your documents.",
              type: "secret",
              maxLength: 64,
              pattern: "[A-Za-z0-9 /-]{4,64}",
              patternHint: "letters, digits, spaces, slashes or dashes",
              bind: { kind: "secure", name: "id_number" },
              fill: ["assignee"],
            },
          ],
        },
      ],
    },
  ],
};
